import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import {
  Table,
  Button,
  Tag,
  Space,
  Popconfirm,
  message,
  Switch,
  Tooltip,
  Input,
  Select,
} from 'antd';
import {
  PlusOutlined,
  LineChartOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';
import { PUBLIC_LINK_BASE, CHANNEL_OPTIONS, CHANNEL_COLORS } from './constants';

interface TrackingLink {
  id: number;
  slug: string;
  name: string;
  channel: string;
  targetUrl: string;
  description: string | null;
  isActive: boolean;
  /** Total raw clicks (every redirect counted). */
  clickCount: number;
  /** Unique visitors deduped by `sg_v` cookie (legacy rows fall back to
   *  IP + user-agent). Same device clicking N times = 1. */
  uniqueClickCount: number;
  createdAt: string;
  createdByStaff: { id: number; fullName: string | null; username: string } | null;
}

export const TrackingLinkList = () => {
  const { create, show } = useNavigation();
  const { can } = usePermission();
  const [channelFilter, setChannelFilter] = useState<string | undefined>();
  const [isActiveFilter, setIsActiveFilter] = useState<string | undefined>();
  const [search, setSearch] = useState<string | undefined>();

  const { tableProps, tableQueryResult, setFilters } = useTable<TrackingLink>({
    resource: 'admin/tracking-links',
    syncWithLocation: true,
    // Marketing wants to see every tracking link on one page so they can
    // scan/sort across the full set without paging. Page size is set high
    // enough to cover any realistic number of campaigns; Tag-style table
    // layout means even a few hundred rows render fine.
    pagination: { pageSize: 1000, mode: 'off' },
    filters: {
      initial: [],
    },
  });

  // Apply filters imperatively — useTable's `permanent` filters don't refetch
  // when they clear to empty, so clearing channel/status/search left the list
  // stuck on the previous result.
  useEffect(() => {
    setFilters(
      [
        ...(channelFilter
          ? [{ field: 'channel', operator: 'eq' as const, value: channelFilter }]
          : []),
        ...(isActiveFilter !== undefined
          ? [
              {
                field: 'isActive',
                operator: 'eq' as const,
                value: isActiveFilter === 'true',
              },
            ]
          : []),
        ...(search
          ? [{ field: 'search', operator: 'eq' as const, value: search }]
          : []),
      ],
      'replace',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelFilter, isActiveFilter, search]);

  const publicUrl = (slug: string) => `${PUBLIC_LINK_BASE}/${slug}`;

  const copyLink = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(publicUrl(slug));
      message.success(t('common.copied') || 'Copied');
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/tracking-links/${id}/toggle`);
      message.success(t('common.success'));
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/tracking-links/${id}`);
      message.success(t('common.success'));
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  return (
    <List
      title={t('nav.trackingLinks')}
      headerButtons={
        <Space wrap>
          <Select
            allowClear
            placeholder="Kanal"
            style={{ width: 160 }}
            value={channelFilter}
            onChange={(v) => setChannelFilter(v)}
            options={CHANNEL_OPTIONS}
          />
          <Select
            allowClear
            placeholder="Holat"
            style={{ width: 120 }}
            value={isActiveFilter}
            onChange={(v) => setIsActiveFilter(v)}
            options={[
              { value: 'true', label: 'Faol' },
              { value: 'false', label: 'Nofaol' },
            ]}
          />
          <Input.Search
            placeholder="Nomi / slug / URL"
            allowClear
            style={{ width: 240 }}
            onSearch={(v) => setSearch(v || undefined)}
          />
          {can('tracking-links', 'create') && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => create('admin/tracking-links')}
            >
              {t('trackingLinks.new') || 'Yangi havola'}
            </Button>
          )}
        </Space>
      }
    >
      <Table {...tableProps} rowKey="id" size="small" pagination={false}>
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column
          dataIndex="name"
          title={t('trackingLinks.name') || 'Nomi'}
          render={(v: string, record: TrackingLink) => (
            <div>
              <div style={{ fontWeight: 600 }}>{v}</div>
              {record.description && (
                <div style={{ fontSize: 12, color: '#999' }}>
                  {record.description}
                </div>
              )}
            </div>
          )}
        />
        <Table.Column
          dataIndex="channel"
          title={t('trackingLinks.channel') || 'Kanal'}
          width={140}
          render={(v: string) => (
            <Tag color={CHANNEL_COLORS[v] || 'default'}>
              {CHANNEL_OPTIONS.find((o) => o.value === v)?.label || v}
            </Tag>
          )}
        />
        <Table.Column
          title={t('trackingLinks.link') || 'Havola'}
          width={280}
          render={(_, record: TrackingLink) => (
            <Space>
              <Tooltip title={publicUrl(record.slug)}>
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 13,
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  {publicUrl(record.slug)}
                </span>
              </Tooltip>
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copyLink(record.slug)}
              />
            </Space>
          )}
        />
        <Table.Column
          dataIndex="clickCount"
          title={t('trackingLinks.clicks') || 'Bosilgan'}
          width={110}
          sorter
          render={(v: number) => (
            <Tooltip title="Jami bosilishlar (har bir qayta bosish ham hisoblanadi)">
              <Tag color={v > 0 ? 'blue' : 'default'}>{v}</Tag>
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="uniqueClickCount"
          title="Unikal"
          width={110}
          render={(v: number, record: TrackingLink) => (
            <Tooltip title="Bir qurilma (cookie / IP + brauzer) faqat bir marta hisoblanadi">
              <Tag color={v > 0 ? 'cyan' : 'default'}>
                {v}
                {record.clickCount > 0 && v > 0 && (
                  <span style={{ marginLeft: 4, color: '#888', fontSize: 11 }}>
                    ({Math.round((v / record.clickCount) * 100)}%)
                  </span>
                )}
              </Tag>
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="isActive"
          title="Faol"
          width={80}
          render={(v: boolean, record: TrackingLink) =>
            can('tracking-links', 'update') ? (
              <Switch checked={v} onChange={() => handleToggle(record.id)} />
            ) : (
              <Tag color={v ? 'green' : 'red'}>{v ? 'Faol' : 'Nofaol'}</Tag>
            )
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Yaratilgan"
          width={120}
          render={(v: string) => new Date(v).toLocaleDateString('uz-UZ')}
        />
        <Table.Column
          title="Amallar"
          width={130}
          render={(_, record: TrackingLink) => (
            <Space>
              <Tooltip title="Statistika">
                <Button
                  size="small"
                  type="text"
                  icon={<LineChartOutlined />}
                  onClick={() => show('admin/tracking-links', record.id)}
                />
              </Tooltip>
              {can('tracking-links', 'delete') && (
                <Popconfirm
                  title="O'chirishni tasdiqlaysizmi?"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
